/**
 * DataProtectionPage — /protection-des-donnees
 *
 * Page publique pédagogique, lisible en < 2 minutes. Design Réseau Light.
 * Respecte strictement le brief fondateur :
 * - Aucune durée non validée juridiquement n'est affichée comme définitive.
 * - Vocabulaire précis (« pseudonymisé » vs « anonymisé »).
 * - 3 repères visuels d'introduction + 5 sections courtes.
 * - CTA « Exercer mes droits » → parcours sécurisé.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ShieldCheck, Lock, Clock, ChevronRight, Mail, FileText,
    UserCheck, EyeOff, ArrowRight, Loader2, X, CheckCircle2
} from 'lucide-react';
import api from '../lib/api';

const PILLARS = [
    {
        icon: UserCheck,
        title: 'Vous gardez le contrôle',
        body: 'Vos coordonnées ne sont partagées qu\'avec les professionnels que vous choisissez. Aucune publicité, aucun revente de données.',
    },
    {
        icon: Lock,
        title: 'Accès limité aux personnes autorisées',
        body: 'Les notes internes, mandats et coordonnées vendeurs sont strictement réservés aux professionnels concernés. Pas de partage public.',
    },
    {
        icon: Clock,
        title: 'Conservation limitée, droits accessibles',
        body: 'Nous conservons vos données le temps nécessaire à votre projet et à nos obligations. Vous pouvez les consulter, corriger ou supprimer à tout moment.',
    },
];

const SECTIONS = [
    {
        id: 'donnees-utiles',
        eyebrow: 'Section A',
        title: 'Les données utiles, uniquement',
        body: [
            'Homly Réseau collecte uniquement les informations nécessaires aux services que vous utilisez :',
            null,
            ['Coordonnées que vous nous transmettez volontairement (nom, email, téléphone, message libre).',
             'Besoin immobilier exprimé lors d\'une demande (type de projet, zone).',
             'Avis que vous laissez à la suite d\'un parcours vérifiable (mandat existant chez le professionnel).',
             'Informations professionnelles qu\'un agent publie sur son profil.',
             'Événements techniques nécessaires à la sécurité du service (connexion, signalement).'],
            null,
            'Une simple consultation d\'un profil ne crée pas automatiquement une demande vendeur. Aucune trace n\'est laissée tant que vous n\'avez pas envoyé de message ou pris contact.',
        ],
    },
    {
        id: 'pas-publiques',
        eyebrow: 'Section B',
        title: 'Vos données ne sont pas publiques par défaut',
        body: [
            'Sauf indication contraire de votre part, vos informations restent privées entre vous et le professionnel concerné.',
            null,
            ['Vos coordonnées de vendeur ne sont accessibles qu\'aux professionnels autorisés à les recevoir.',
             'Les données d\'un projet immobilier (adresse précise, prix attendu, raisons de la vente) ne sont jamais affichées publiquement.',
             'L\'identité publique d\'un auteur d\'avis dépend de son choix : initiale, prénom, anonyme.',
             'Les notes internes prises par un professionnel dans son cockpit restent strictement privées.',
             'Aucune donnée confidentielle de mandat (acquéreurs, offres) n\'est rendue publique.'],
        ],
    },
    {
        id: 'contexte-limite',
        eyebrow: 'Section C',
        title: 'Un contexte limité et contrôlé',
        body: [
            'Lorsque vous nous y autorisez explicitement, nous pouvons transmettre au professionnel un résumé très court du contexte précédant votre demande.',
            null,
            'Exemple : « Le vendeur s\'est intéressé à votre expérience locale et à vos avis vérifiés. »',
            null,
            'Nous ne transmettons jamais :',
            null,
            ['Votre historique détaillé de navigation.',
             'Le temps que vous avez passé sur chaque page.',
             'Les profils concurrents que vous avez consultés.',
             'Des déductions psychologiques ou comportementales.',
             'Toute navigation sans lien avec le besoin que vous avez exprimé.'],
            null,
            'Vous pouvez retirer ce consentement à tout moment depuis la section « Vos droits » ci-dessous.',
        ],
    },
    {
        id: 'conservation',
        eyebrow: 'Section D',
        title: 'Conservation et pseudonymisation',
        body: [
            'Nous appliquons des principes clairs en matière de durée de conservation :',
            null,
            ['Conservation limitée à la finalité du service.',
             'Durées différentes selon le statut de la demande (active, clôturée, non convertie, spam).',
             'Pseudonymisation lorsque les données personnelles ne sont plus strictement nécessaires (vos PII sont remplacées par des identifiants techniques sans valeur pour des tiers).',
             'Conservation possible de statistiques réellement anonymisées (sans possibilité raisonnable d\'identification individuelle).',
             'Les données de démonstration et de test sont strictement exclues des traitements réels.'],
            null,
            'Les durées précises applicables sont détaillées dans notre politique de confidentialité et peuvent varier selon la nature du service et les obligations légales. Nous ne publions ici que des principes vérifiables.',
        ],
    },
    {
        id: 'mes-droits',
        eyebrow: 'Section E',
        title: 'Vos droits',
        body: [
            'Conformément à la réglementation applicable, vous disposez à tout moment des droits suivants sur les données qui vous concernent :',
            null,
            ['Accès — savoir quelles données nous détenons sur vous.',
             'Rectification — corriger des informations inexactes.',
             'Suppression — demander l\'effacement de vos données (sous réserve d\'obligations légales contraires).',
             'Retrait d\'un consentement facultatif (contexte, contact futur).',
             'Opposition au traitement lorsque cela est applicable.',
             'Export — obtenir une copie de vos données dans un format réutilisable.',
             'Contact direct pour exercer ces droits.'],
            null,
            'Nous vérifions votre identité avant toute action sensible. Aucune demande de suppression ou de rectification n\'est traitée sur la seule base d\'un email non vérifié.',
        ],
    },
];


function RightsModal({ open, onClose }) {
    const [step, setStep] = useState('choose');
    const [requestType, setRequestType] = useState('access');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [previewLink, setPreviewLink] = useState(null);

    const REQUEST_TYPES = [
        { value: 'access', label: 'Consulter mes données', hint: 'Voir un résumé de ce que nous détenons.' },
        { value: 'rectification', label: 'Corriger mes informations', hint: 'Mettre à jour prénom, email, téléphone ou message.' },
        { value: 'deletion', label: 'Demander la suppression', hint: 'Effacer mes intentions vendeurs (sous 7 jours).' },
        { value: 'consent_withdrawal', label: 'Retirer un consentement', hint: 'Stopper les usages facultatifs (analytics, contact futur).' },
    ];

    const submit = async () => {
        if (!email.trim()) { toast.error('Indiquez votre email.'); return; }
        setSubmitting(true);
        try {
            const r = await api.post('/public/seller-rights/request-token', {
                email: email.trim(),
                request_type: requestType,
            });
            setPreviewLink(r.data?._preview_url || null);
            setStep('success');
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) toast.error('Trop de demandes récentes. Réessayez dans une heure.');
            else if (status === 400) toast.error(e?.response?.data?.detail || 'Demande invalide.');
            else toast.error('Erreur d\'envoi. Réessayez dans un instant.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;
    return (
        <div data-reseau="light" className="reseau-light fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
             style={{ background: 'var(--reseau-surface-overlay)' }} onClick={onClose}>
            <div className="reseau-card w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
                 style={{ borderRadius: 'var(--reseau-radius-xl) var(--reseau-radius-xl) 0 0' }}
                 onClick={e => e.stopPropagation()} data-testid="rights-modal">
                <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between mb-3">
                        <h2 className="reseau-h2 text-xl">
                            {step === 'success' ? 'Lien envoyé' : 'Exercer mes droits'}
                        </h2>
                        <button onClick={onClose} className="reseau-btn-ghost p-2 rounded-full" aria-label="Fermer" data-testid="rights-close-btn">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {step === 'choose' && (
                        <>
                            <p className="text-sm mb-4" style={{ color: 'var(--reseau-ink-muted)' }}>
                                Nous vous enverrons un lien sécurisé sur votre email pour confirmer votre identité.
                                Aucune action n'est prise sans vérification.
                            </p>
                            <div className="space-y-2">
                                {REQUEST_TYPES.map(t => (
                                    <label key={t.value} className="flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors"
                                           style={{
                                               border: '1px solid ' + (requestType === t.value ? 'var(--reseau-violet)' : 'var(--reseau-border)'),
                                               background: requestType === t.value ? 'var(--reseau-violet-soft)' : 'var(--reseau-surface)',
                                           }}
                                           data-testid={`rights-type-${t.value}`}>
                                        <input type="radio" name="rtype" value={t.value} checked={requestType === t.value}
                                               onChange={() => setRequestType(t.value)} className="mt-1 accent-purple-700" />
                                        <div>
                                            <p className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>{t.label}</p>
                                            <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>{t.hint}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <div className="mt-4">
                                <label className="text-xs font-medium" style={{ color: 'var(--reseau-ink-soft)' }}>Votre email *</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                       className="reseau-input mt-1" placeholder="vous@email.fr"
                                       data-testid="rights-email-input" />
                            </div>
                            <button onClick={submit} disabled={submitting || !email.trim()}
                                    className="reseau-btn reseau-btn-primary w-full mt-4"
                                    data-testid="rights-submit-btn">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Recevoir le lien sécurisé'}
                            </button>
                        </>
                    )}

                    {step === 'success' && (
                        <div className="text-center py-4" data-testid="rights-success">
                            <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
                                 style={{ background: 'var(--reseau-success-soft)' }}>
                                <CheckCircle2 className="w-7 h-7" style={{ color: 'var(--reseau-success)' }} />
                            </div>
                            <p className="text-sm" style={{ color: 'var(--reseau-ink-soft)' }}>
                                Si l'email existe dans nos systèmes, un lien sécurisé vous a été envoyé. Vérifiez votre boîte (et les spams).
                                Ce lien expire au bout de 24 heures.
                            </p>
                            {previewLink && (
                                <div className="mt-4 p-3 rounded-lg text-xs text-left"
                                     style={{ background: 'var(--reseau-warning-soft)', color: 'var(--reseau-warning)' }}>
                                    <strong>Aperçu (preview/démo uniquement) :</strong><br />
                                    <Link to={previewLink} className="underline break-all" data-testid="rights-preview-link">
                                        {previewLink}
                                    </Link>
                                </div>
                            )}
                            <button onClick={onClose} className="reseau-btn reseau-btn-primary mt-4">
                                Continuer
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


export default function DataProtectionPage() {
    const [rightsOpen, setRightsOpen] = useState(false);
    const navigate = useNavigate();

    // Si l'URL contient #mes-droits, ouvrir directement la modale
    React.useEffect(() => {
        if (window.location.hash === '#mes-droits') {
            setRightsOpen(true);
            // Décale le scroll vers la section E
            setTimeout(() => {
                document.getElementById('mes-droits')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    }, []);

    return (
        <div data-reseau="light" className="reseau-light min-h-screen" style={{ background: 'var(--reseau-bg)' }}>
            {/* Header sobre */}
            <header className="sticky top-0 z-30 border-b"
                    style={{ background: 'rgba(250, 250, 247, 0.92)', backdropFilter: 'blur(12px)',
                             borderColor: 'var(--reseau-border)' }}>
                <div className="mx-auto px-4 sm:px-6 h-14 flex items-center justify-between"
                     style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                    <Link to="/reseau" className="flex items-center gap-2" data-testid="data-prot-logo">
                        <span className="text-xl font-extrabold reseau-gradient-text"
                              style={{ fontFamily: 'var(--reseau-font-display)' }}>Homly</span>
                        <span style={{ color: 'var(--reseau-orange)' }}>.</span>
                    </Link>
                    <button onClick={() => navigate(-1)} className="reseau-btn reseau-btn-ghost text-sm"
                            style={{ padding: '8px 12px' }} data-testid="data-prot-back">
                        ← Retour
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="border-b" style={{ background: 'var(--reseau-bg-soft)', borderColor: 'var(--reseau-border)' }}>
                <div className="mx-auto px-4 sm:px-6 py-10 sm:py-14" style={{ maxWidth: 'var(--reseau-maxw-prose)' }}>
                    <p className="reseau-eyebrow mb-3">Confidentialité et droits</p>
                    <h1 className="reseau-h1 text-3xl sm:text-4xl mb-4" data-testid="data-prot-title">
                        Comment Homly protège vos données
                    </h1>
                    <p className="text-base sm:text-lg" style={{ color: 'var(--reseau-ink-soft)' }}>
                        En moins de deux minutes, voici ce que nous faisons concrètement avec vos informations,
                        pour quelles raisons, et comment vous gardez la main.
                    </p>
                </div>
            </section>

            {/* 3 piliers */}
            <section className="mx-auto px-4 sm:px-6 py-8" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pillars">
                    {PILLARS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <div key={i} className="reseau-card p-5" data-testid={`pillar-${i}`}>
                                <div className="w-10 h-10 rounded-full mb-3 flex items-center justify-center"
                                     style={{ background: 'var(--reseau-violet-soft)' }}>
                                    <Icon className="w-5 h-5" style={{ color: 'var(--reseau-violet)' }} />
                                </div>
                                <h2 className="reseau-h2 text-base mb-1">{p.title}</h2>
                                <p className="text-sm" style={{ color: 'var(--reseau-ink-soft)' }}>{p.body}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Sections détaillées */}
            <main className="mx-auto px-4 sm:px-6 pb-12" style={{ maxWidth: 'var(--reseau-maxw-prose)' }}>
                {SECTIONS.map(s => (
                    <section key={s.id} id={s.id} className="mt-10 sm:mt-12" data-testid={`section-${s.id}`}>
                        <p className="reseau-eyebrow mb-2">{s.eyebrow}</p>
                        <h2 className="reseau-h2 text-xl sm:text-2xl mb-4">{s.title}</h2>
                        <div className="space-y-3 text-sm sm:text-base" style={{ color: 'var(--reseau-ink-soft)' }}>
                            {s.body.map((line, i) => {
                                if (line === null) return <div key={i} className="h-1" />;
                                if (Array.isArray(line)) {
                                    return (
                                        <ul key={i} className="space-y-2 ml-1">
                                            {line.map((item, j) => (
                                                <li key={j} className="flex items-start gap-2">
                                                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"
                                                                  style={{ color: 'var(--reseau-violet)' }} />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    );
                                }
                                return <p key={i}>{line}</p>;
                            })}
                        </div>

                        {s.id === 'mes-droits' && (
                            <div className="mt-6 p-5 reseau-card-soft" data-testid="rights-cta-block">
                                <p className="text-sm font-semibold mb-3" style={{ color: 'var(--reseau-ink)' }}>
                                    Exercer mes droits en quelques clics
                                </p>
                                <p className="text-xs mb-4" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Vous recevrez un lien sécurisé sur votre email pour vérifier votre identité.
                                    Le lien expire après 24 heures et ne peut être utilisé qu'une seule fois.
                                </p>
                                <button onClick={() => setRightsOpen(true)}
                                        className="reseau-btn reseau-btn-primary"
                                        data-testid="exercise-rights-cta">
                                    <Mail className="w-4 h-4" />
                                    Exercer mes droits
                                </button>
                            </div>
                        )}
                    </section>
                ))}

                {/* Liens documents juridiques */}
                <section className="mt-12 pt-8 border-t" style={{ borderColor: 'var(--reseau-border)' }}>
                    <h2 className="reseau-h2 text-base mb-3">Documents complets</h2>
                    <p className="text-sm mb-4" style={{ color: 'var(--reseau-ink-muted)' }}>
                        Cette page pédagogique ne remplace pas nos documents juridiques officiels.
                    </p>
                    <ul className="space-y-2 text-sm">
                        <li>
                            <Link to="/privacy" className="inline-flex items-center gap-1.5 hover:underline"
                                  style={{ color: 'var(--reseau-violet)' }} data-testid="link-privacy">
                                <FileText className="w-4 h-4" /> Politique de confidentialité
                                <ChevronRight className="w-3 h-3" />
                            </Link>
                        </li>
                        <li>
                            <Link to="/cgu" className="inline-flex items-center gap-1.5 hover:underline"
                                  style={{ color: 'var(--reseau-violet)' }} data-testid="link-cgu">
                                <FileText className="w-4 h-4" /> Conditions générales d'utilisation
                                <ChevronRight className="w-3 h-3" />
                            </Link>
                        </li>
                        <li>
                            <Link to="/legal" className="inline-flex items-center gap-1.5 hover:underline"
                                  style={{ color: 'var(--reseau-violet)' }} data-testid="link-legal">
                                <FileText className="w-4 h-4" /> Mentions légales
                                <ChevronRight className="w-3 h-3" />
                            </Link>
                        </li>
                    </ul>
                </section>
            </main>

            {/* Footer */}
            <footer className="border-t" style={{ borderColor: 'var(--reseau-border)', background: 'var(--reseau-surface)' }}>
                <div className="mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm"
                     style={{ maxWidth: 'var(--reseau-maxw-page)', color: 'var(--reseau-ink-muted)' }}>
                    <span>Cette page est susceptible d'évoluer. Dernière mise à jour : juin 2026.</span>
                    <Link to="/reseau" className="hover:underline">Retour au réseau</Link>
                </div>
            </footer>

            <RightsModal open={rightsOpen} onClose={() => setRightsOpen(false)} />
        </div>
    );
}
